import React from 'react';
import {
  Box,
  IconButton,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import PictureInPictureAltOutlinedIcon from '@mui/icons-material/PictureInPictureAltOutlined';

import BetaChip from '../utils/BetaChip';

const DRAG_CLICK_TOLERANCE_PX = 3;

const stopMoving = (event: React.MouseEvent<HTMLElement>) => event.stopPropagation();

const PinguChatHeader = ({
  sessionName,
  canRename,
  onRename,
  onStartMove,
  onStartNewSession,
  onToggleSessions,
  onClose,
  historyButtonRef,
  docked = false,
  onToggleMode,
}: {
  sessionName: string;
  canRename: boolean;
  onRename: (name: string) => Promise<boolean>;
  onStartMove: (event: React.MouseEvent<HTMLElement>) => void;
  onStartNewSession: () => void;
  onToggleSessions: () => void;
  onClose: () => void;
  historyButtonRef?: React.Ref<HTMLButtonElement>;
  docked?: boolean;
  // Left out where the window is too narrow to dock.
  onToggleMode?: () => void;
}) => {
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState(sessionName);
  const [saving, setSaving] = React.useState(false);
  const pressedAt = React.useRef<{ x: number; y: number } | null>(null);

  React.useEffect(() => setName(sessionName), [sessionName]);

  const saveName = async (): Promise<void> => {
    if (!name.trim() || saving) return;
    setSaving(true);
    const saved = await onRename(name);
    setSaving(false);
    if (saved) setEditing(false);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (docked) return;
    pressedAt.current = { x: event.clientX, y: event.clientY };
    onStartMove(event);
  };

  // The name sits inside the drag area, so releasing a drag on it must not open rename.
  // Keyboard clicks (detail 0) report 0,0 and are never drags; the press is cleared so it
  // can't affect a later click.
  const handleNameClick = (event: React.MouseEvent<HTMLElement>) => {
    const start = pressedAt.current;
    pressedAt.current = null;
    const dragged = event.detail !== 0 && start !== null && (
      Math.abs(event.clientX - start.x) > DRAG_CLICK_TOLERANCE_PX
      || Math.abs(event.clientY - start.y) > DRAG_CLICK_TOLERANCE_PX
    );
    if (!dragged) setEditing(true);
  };

  const nameSx = {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  // Docked, the header's content is the navbar's height and its own bottom border
  // sits just below it, so the border's top edge meets the bottom edge of the navbar.
  // content-box is explicit because CssBaseline makes elements inherit border-box.
  const dockedSx = (theme: Theme) => ({
    ...theme.mixins.toolbar,
    py: 0,
    boxSizing: 'content-box' as const,
    borderBottom: `1px solid ${theme.palette.divider}`,
  });

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={[
        {
          px: 4,
          py: 3,
          gap: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
          cursor: docked ? 'default' : 'move',
          userSelect: 'none',
        },
        docked && dockedSx,
      ]}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, minWidth: 0,
        }}
        >
          <Typography variant="largeTitle" noWrap sx={{ display: 'block', minWidth: 0 }}>
            Pingwin
          </Typography>
          <BetaChip service="Pingwin" onMouseDown={stopMoving} />
        </Box>
        {editing && (
          <TextField
            autoFocus
            variant="standard"
            size="small"
            value={name}
            disabled={saving}
            onMouseDown={stopMoving}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                saveName();
              }
              if (event.key === 'Escape') {
                setName(sessionName);
                setEditing(false);
              }
            }}
            inputProps={{ 'aria-label': 'Conversation name' }}
            sx={{ display: 'flex', cursor: 'text', '& input': { typography: 'body1', py: 0 } }}
          />
        )}
        {!editing && canRename && (
          <Typography
            component="button"
            type="button"
            variant="body1"
            color="text.secondary"
            aria-label={`Rename conversation: ${sessionName}`}
            title={sessionName || undefined}
            onClick={handleNameClick}
            sx={{
              ...nameSx,
              textAlign: 'left',
              border: 0,
              background: 'transparent',
              cursor: 'text',
              p: 0,
            }}
          >
            {sessionName || 'New conversation'}
          </Typography>
        )}
        {!editing && !canRename && (
          <Typography variant="body1" color="text.secondary" sx={nameSx}>
            New conversation
          </Typography>
        )}
      </Box>
      <Box
        onMouseDown={stopMoving}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'default',
        }}
      >
        <Tooltip title="New conversation">
          <IconButton size="small" onClick={onStartNewSession} aria-label="New conversation">
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Previous conversations">
          <IconButton
            size="small"
            onClick={onToggleSessions}
            ref={historyButtonRef}
            aria-label="Previous conversations"
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {onToggleMode && (
          <Tooltip title={docked ? 'Float over the page' : 'Dock beside the page'}>
            <IconButton
              size="small"
              onClick={onToggleMode}
              aria-label={docked ? 'Float over the page' : 'Dock beside the page'}
            >
              {docked
                ? <PictureInPictureAltOutlinedIcon fontSize="small" />
                : <ViewSidebarOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ ml: 2 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default PinguChatHeader;
